/**
 * 태어난 시각 → 시주(時) 미리 보기 (귀인도감 client/src/components/pickers/dateUtils.js 의 branchOfTime 자리, D79).
 *
 * 진태양시 = 시계 시각 + 경도 보정 + 균시차. 우리 앱은 출생 지역을 받지 않아 서버처럼 늘 서울(경도 126.978°)로 본다.
 * ⚠️ 균시차 식은 서버 `server/src/services/saju/manseryeok.js` 의 `equationOfTimeMin` 과 같다(NOAA, KST 정오 기준).
 *    미리 보기와 저장되는 시주가 갈리면 안 돼서 복제하고, 같은 날짜에 같은 값을 시험으로 고정한다(solarTime.test.js).
 */

/** 서울 경도 보정(분): (126.978 − 135) × 4 */
export const SEOUL_OFFSET_MIN = (126.978 - 135) * 4;

/** 시각(0~23시) → 시지. 진태양시로 보정한 뒤에 쓴다 */
export const HOUR_BRANCHES = [
    '자',
    '축',
    '축',
    '인',
    '인',
    '묘',
    '묘',
    '진',
    '진',
    '사',
    '사',
    '오',
    '오',
    '미',
    '미',
    '신',
    '신',
    '유',
    '유',
    '술',
    '술',
    '해',
    '해',
    '자',
];

function julianDayNumber(year, month, day) {
    const a = Math.floor((14 - month) / 12);
    const yy = year + 4800 - a;
    const mm = month + 12 * a - 3;
    return (
        day +
        Math.floor((153 * mm + 2) / 5) +
        365 * yy +
        Math.floor(yy / 4) -
        Math.floor(yy / 100) +
        Math.floor(yy / 400) -
        32045
    );
}

/** 균시차(분) — 진태양시 − 평균태양시. 'YYYY-MM-DD'(양력)가 아니면 0 */
export function equationOfTimeMin(isoDate) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(isoDate ?? ''));
    if (!match) {
        return 0;
    }
    const jd = julianDayNumber(Number(match[1]), Number(match[2]), Number(match[3])) - 0.5 + 3 / 24;
    const T = (jd - 2451545) / 36525;
    const rad = Math.PI / 180;
    const L0 = (280.46646 + T * (36000.76983 + T * 0.0003032)) % 360;
    const M = 357.52911 + T * (35999.05029 - 0.0001537 * T);
    const ecc = 0.016708634 - T * (0.000042037 + 0.0000001267 * T);
    const eps0 = 23 + (26 + (21.448 - T * (46.815 + T * (0.00059 - T * 0.001813))) / 60) / 60;
    const eps = eps0 + 0.00256 * Math.cos((125.04 - 1934.136 * T) * rad);
    const y = Math.tan((eps * rad) / 2) ** 2;
    return (
        (4 / rad) *
        (y * Math.sin(2 * L0 * rad) -
            2 * ecc * Math.sin(M * rad) +
            4 * ecc * y * Math.sin(M * rad) * Math.cos(2 * L0 * rad) -
            0.5 * y * y * Math.sin(4 * L0 * rad) -
            1.25 * ecc * ecc * Math.sin(2 * M * rad))
    );
}

/**
 * 고른 시각의 시지(서울 진태양시). 날짜(양력)를 주면 균시차까지 넣는다
 * @param {number} hour  0~23
 * @param {number} minute  0~59
 * @param {string|null} [isoDate]  양력 생년월일
 */
export function branchOfTime(hour, minute, isoDate = null) {
    const total = hour * 60 + minute + SEOUL_OFFSET_MIN + (isoDate ? equationOfTimeMin(isoDate) : 0);
    const wrapped = ((total % 1440) + 1440) % 1440;
    return HOUR_BRANCHES[Math.floor(wrapped / 60)];
}
